import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import ScrollToTop from '@/components/base/ScrollToTop';
import { aboutInfo } from '@/mocks/about';
import { teamImageMap } from '@/mocks/teamImages';

const breadcrumbItems = [
  { label: '首页', path: '/' },
  { label: '关于我们' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q52p7nahb7382ov6ag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于膀胱健康管理网',
    description: aboutInfo.brandIntro,
    url: 'https://bladderhealth.cn/about',
    inLanguage: 'zh-CN',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: aboutInfo.brandName,
    url: 'https://bladderhealth.cn',
    description: aboutInfo.brandIntro,
    foundingDate: '2020-03',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '海淀区',
      streetAddress: '中关村南大街5号院卫生健康大厦1206室',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: aboutInfo.contactInfo.phone,
      contactType: 'customer service',
      email: aboutInfo.contactInfo.email,
      availableLanguage: 'Chinese',
    },
  };

  return (
    <>
      <SchemaOrg data={aboutSchema} />
      <SchemaOrg data={orgSchema} />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 md:pt-28 pb-12 md:pb-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="mb-6">
                <Breadcrumb items={breadcrumbItems} />
              </div>

              <h1 className="font-heading text-xl md:text-3xl text-foreground-950 mb-2">
                关于我们
              </h1>
              <p className="text-sm text-foreground-500">
                了解膀胱健康管理网的品牌故事与专业团队
              </p>
            </div>
          </div>

          {/* Brand Introduction */}
          <section className="mt-8 md:mt-10">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <div className="bg-background-50 border border-background-200/60 rounded-lg p-6 md:p-8">
                  <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-4">
                    {aboutInfo.brandName}
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {aboutInfo.brandIntro}
                  </p>
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-background-100 rounded-lg p-4">
                      <h3 className="font-heading text-sm text-foreground-950 mb-1">我们的使命</h3>
                      <p className="text-sm text-foreground-600">{aboutInfo.mission}</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4">
                      <h3 className="font-heading text-sm text-foreground-950 mb-1">我们的愿景</h3>
                      <p className="text-sm text-foreground-600">{aboutInfo.vision}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="mt-8 md:mt-10">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {aboutInfo.stats.map((stat) => (
                    <div key={stat.label} className="bg-background-50 border border-background-200/60 rounded-lg p-5 text-center">
                      <div className="w-10 h-10 mx-auto flex items-center justify-center text-accent-500">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <div className="mt-2 text-xl md:text-2xl font-heading text-foreground-950">{stat.value}</div>
                      <div className="text-xs text-foreground-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="mt-10 md:mt-12">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-5 text-center">
                  核心价值观
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {aboutInfo.values.map((val) => (
                    <div key={val.title} className="bg-background-50 border border-background-200/60 rounded-lg p-5 text-center">
                      <div className="w-10 h-10 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-3">
                        <i className={`${val.icon} text-lg text-accent-600`}></i>
                      </div>
                      <h3 className="font-heading text-sm text-foreground-950 mb-1">{val.title}</h3>
                      <p className="text-xs text-foreground-600 leading-relaxed">{val.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="mt-10 md:mt-12">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-5 text-center">
                  发展历程
                </h2>
                <div className="space-y-0">
                  {aboutInfo.milestones.map((milestone, index) => (
                    <div key={milestone.year} className="flex gap-4 md:gap-6">
                      <div className="flex flex-col items-center">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-1.5 ${
                          index === aboutInfo.milestones.length - 1 ? 'bg-primary-500' : 'bg-primary-300'
                        }`}></div>
                        {index < aboutInfo.milestones.length - 1 && (
                          <div className="w-0.5 flex-1 bg-background-300 mt-1"></div>
                        )}
                      </div>
                      <div className={`pb-6 ${index === aboutInfo.milestones.length - 1 ? 'pb-0' : ''}`}>
                        <span className="text-xs text-primary-600 font-medium">{milestone.year}</span>
                        <h3 className="font-heading text-sm text-foreground-950 mt-0.5">{milestone.title}</h3>
                        <p className="text-xs text-foreground-600 mt-1 leading-relaxed">{milestone.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Team */}
          <section className="mt-10 md:mt-12">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-5 text-center">
                  专家团队
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {aboutInfo.teamMembers.map((member) => (
                    <div key={member.name} className="bg-background-50 border border-background-200/60 rounded-lg p-5 text-center">
                      <div className="w-16 h-16 mx-auto rounded-full overflow-hidden mb-3">
                        <img
                          src={teamImageMap[member.imageSeq]}
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h3 className="font-heading text-sm text-foreground-950">{member.name}</h3>
                      <p className="text-xs text-primary-600 mt-0.5">{member.role}</p>
                      <p className="text-xs text-foreground-600 mt-2 leading-relaxed">{member.bio}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Certifications */}
          <section className="mt-10 md:mt-12">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-5 text-center">
                  资质认证
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {aboutInfo.certifications.map((cert) => (
                    <div key={cert.name} className="bg-background-100 rounded-lg p-5 flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-verified-badge-line text-sm text-primary-600"></i>
                      </div>
                      <div>
                        <h3 className="font-heading text-sm text-foreground-950">{cert.name}</h3>
                        <p className="text-xs text-foreground-600 mt-1">{cert.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="mt-10 md:mt-12">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-5 text-center">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Contact Info */}
                  <div className="bg-background-100 rounded-lg p-6 md:p-8">
                    <h3 className="font-heading text-base text-foreground-950 mb-4">联系方式</h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-map-pin-line text-sm text-primary-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500 mb-0.5">地址</p>
                          <p className="text-sm text-foreground-800">{aboutInfo.contactInfo.address}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-phone-line text-sm text-primary-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500 mb-0.5">电话</p>
                          <p className="text-sm text-foreground-800">{aboutInfo.contactInfo.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-mail-line text-sm text-primary-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500 mb-0.5">邮箱</p>
                          <p className="text-sm text-foreground-800">{aboutInfo.contactInfo.email}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-wechat-line text-sm text-primary-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500 mb-0.5">微信</p>
                          <p className="text-sm text-foreground-800">{aboutInfo.contactInfo.wechat}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-time-line text-sm text-primary-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500 mb-0.5">工作时间</p>
                          <p className="text-sm text-foreground-800">{aboutInfo.contactInfo.workHours}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Form */}
                  <div className="bg-background-50 border border-background-200/60 rounded-lg p-6 md:p-8">
                    <h3 className="font-heading text-base text-foreground-950 mb-4">发送消息</h3>
                    <form
                      id="contact-form"
                      data-readdy-form
                      onSubmit={handleSubmit}
                      className="space-y-4"
                    >
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1">
                          姓名 <span className="text-accent-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1">
                          邮箱 <span className="text-accent-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1">
                          主题
                        </label>
                        <input
                          type="text"
                          id="contact-subject"
                          name="subject"
                          className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                          placeholder="请输入消息主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1">
                          消息 <span className="text-accent-500">*</span>
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                          placeholder="请输入您的消息（最多500字）"
                        ></textarea>
                      </div>

                      {/* Honeypot */}
                      <input
                        type="text"
                        name="phone_alt"
                        className="form-honeypot"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />

                      <button
                        type="submit"
                        className="w-full px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        发送消息
                      </button>

                      {formStatus === 'success' && (
                        <p className="text-sm text-primary-600 text-center">消息已发送，我们会尽快回复您！</p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-sm text-red-600 text-center">{formError}</p>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Bottom CTA */}
          <section className="mt-10 md:mt-12">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto bg-primary-500 rounded-lg p-6 md:p-8 text-center">
                <h2 className="font-heading text-lg md:text-xl text-background-50">
                  开始您的膀胱健康之旅
                </h2>
                <p className="mt-2 text-sm text-background-50/85">
                  浏览我们的专业文章，找到最适合您的健康管理方案
                </p>
                <Link
                  to="/category"
                  className="mt-4 inline-block px-6 py-2.5 rounded-md bg-background-50 text-primary-600 font-medium text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  浏览文章
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}